import { SCHEDULE_START_MINUTE, TOTAL_SCHEDULE_MINUTES } from '../config/constants';
import { formatTimeFromMinutes, timeToMinutes } from './helpers';

/**
 * Utility functions for schedule card resize calculations
 */

/**
 * Convert mouse Y position to time minutes within the schedule grid
 */
export const mouseYToTimeMinutes = (
  mouseY: number,
  gridRect: DOMRect,
  gridHeaderHeight: number,
  totalGridContentHeight: number
): number => {
  // Calculate cursor position relative to grid content (excluding header)
  const contentCursorY = Math.max(0, mouseY - gridRect.top - gridHeaderHeight);
  
  // Convert pixel position to time offset
  const minutesOffset = (contentCursorY / totalGridContentHeight) * TOTAL_SCHEDULE_MINUTES;
  
  // Add to schedule start time
  return SCHEDULE_START_MINUTE + minutesOffset;
};

/**
 * Convert time minutes to Y position within the schedule grid
 */
export const timeMinutesToMouseY = (
  timeMinutes: number,
  gridRect: DOMRect,
  gridHeaderHeight: number,
  totalGridContentHeight: number
): number => {
  // Calculate offset from schedule start
  const minutesOffset = timeMinutes - SCHEDULE_START_MINUTE;
  
  // Convert to pixel position
  const contentY = (minutesOffset / TOTAL_SCHEDULE_MINUTES) * totalGridContentHeight;
  
  // Add grid top and header height
  return gridRect.top + gridHeaderHeight + contentY;
};

/**
 * Calculate time delta from mouse movement
 */
export const calculateTimeDelta = (
  mouseDelta: number,
  totalGridContentHeight: number
): number => {
  // Convert pixel delta to time delta
  return (mouseDelta / totalGridContentHeight) * TOTAL_SCHEDULE_MINUTES;
};

/**
 * Snap time to interval with support for fine adjustment
 */
export const snapTimeToInterval = (
  timeMinutes: number,
  interval: number,
  fineAdjustment: boolean = false,
  fineInterval: number = 1
): number => {
  const snapInterval = fineAdjustment ? fineInterval : interval;
  return Math.round(timeMinutes / snapInterval) * snapInterval;
};

/**
 * Validate time range constraints
 */
export const validateTimeRange = (
  startMinutes: number,
  endMinutes: number,
  minDuration: number,
  scheduleStart: number,
  scheduleEnd: number
): {
  isValid: boolean;
  constrainedStart: number;
  constrainedEnd: number;
  violations: string[];
} => {
  const violations: string[] = [];
  let constrainedStart = startMinutes;
  let constrainedEnd = endMinutes;

  // Check schedule boundaries
  if (startMinutes < scheduleStart) {
    violations.push('Start time before schedule start');
    constrainedStart = scheduleStart;
  }
  
  if (endMinutes > scheduleEnd) {
    violations.push('End time after schedule end');
    constrainedEnd = scheduleEnd;
  }

  // Check minimum duration
  const duration = constrainedEnd - constrainedStart;
  if (duration < minDuration) {
    violations.push('Duration below minimum');
    
    // Adjust to maintain minimum duration
    if (startMinutes !== constrainedStart) {
      // Start was constrained, adjust end
      constrainedEnd = constrainedStart + minDuration;
    } else {
      // End was constrained or neither, adjust start
      constrainedStart = constrainedEnd - minDuration;
    }
  }

  // Final boundary check after duration adjustment
  if (constrainedStart < scheduleStart) {
    constrainedStart = scheduleStart;
    constrainedEnd = constrainedStart + minDuration;
  }
  
  if (constrainedEnd > scheduleEnd) {
    constrainedEnd = scheduleEnd;
    constrainedStart = constrainedEnd - minDuration;
  }

  return {
    isValid: violations.length === 0,
    constrainedStart,
    constrainedEnd,
    violations
  };
};

/**
 * Calculate new times for resize operation
 */
export const calculateResizeNewTimes = (
  originalStartTime: string,
  originalEndTime: string,
  mouseDelta: number,
  resizeEdge: 'top' | 'bottom',
  totalGridContentHeight: number,
  snapInterval: number,
  minDuration: number,
  scheduleStart: number,
  scheduleEnd: number,
  fineAdjustment: boolean = false,
  fineInterval: number = 1
): {
  startTime: string;
  endTime: string;
  isValid: boolean;
  violations: string[];
} => {
  // Convert original times to minutes
  const originalStartMinutes = timeToMinutes(originalStartTime);
  const originalEndMinutes = timeToMinutes(originalEndTime);

  // Handle null values from timeToMinutes
  if (originalStartMinutes === null || originalEndMinutes === null) {
    return {
      startTime: originalStartTime,
      endTime: originalEndTime,
      isValid: false,
      violations: ['Invalid time format']
    };
  }

  // Calculate time delta from mouse movement
  const timeDelta = calculateTimeDelta(mouseDelta, totalGridContentHeight);

  // Apply delta based on resize edge
  let newStartMinutes = originalStartMinutes;
  let newEndMinutes = originalEndMinutes;

  if (resizeEdge === 'top') {
    newStartMinutes = originalStartMinutes + timeDelta;
    // Apply snapping only to the changed time
    newStartMinutes = snapTimeToInterval(newStartMinutes, snapInterval, fineAdjustment, fineInterval);
  } else {
    newEndMinutes = originalEndMinutes + timeDelta;
    // Apply snapping only to the changed time
    newEndMinutes = snapTimeToInterval(newEndMinutes, snapInterval, fineAdjustment, fineInterval);
  }

  // Apply edge-specific constraints
  if (resizeEdge === 'top') {
    // When resizing from top, maintain end time and constrain start time
    newStartMinutes = Math.max(scheduleStart, newStartMinutes);
    newStartMinutes = Math.min(newStartMinutes, originalEndMinutes - minDuration);
  } else {
    // When resizing from bottom, maintain start time and constrain end time
    newEndMinutes = Math.min(scheduleEnd, newEndMinutes);
    newEndMinutes = Math.max(newEndMinutes, originalStartMinutes + minDuration);
  }

  // Final validation
  const validation = validateTimeRange(
    newStartMinutes,
    newEndMinutes,
    minDuration,
    scheduleStart,
    scheduleEnd
  );

  return {
    startTime: formatTimeFromMinutes(validation.constrainedStart),
    endTime: formatTimeFromMinutes(validation.constrainedEnd),
    isValid: validation.isValid,
    violations: validation.violations
  };
};